import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";

/** Winkelwagen-icoon (zelfde lijn als in de header). */
export function CartIcon({ size = 20, className = "" }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
      <path d="M2 3h2.2l2.2 11.2a1.5 1.5 0 0 0 1.5 1.2h8.4a1.5 1.5 0 0 0 1.5-1.2L20 7H5.2" />
    </svg>
  );
}

/**
 * Oranje "configureer"-knop: klein vierkantje met een pijltje, als Link naar een
 * configurator-/productpagina. Tegenhanger van AddToCartButton (verwijzen i.p.v. bestellen).
 */
export function ConfigureButton({
  to,
  ariaLabel,
  size = "sm",
  tone = "accent",
  className = "",
}: {
  to: string;
  ariaLabel: string;
  size?: "sm" | "md";
  /** accent = oranje (configurator); primary = navy (artikel/blog). */
  tone?: "accent" | "primary";
  className?: string;
}) {
  const shape = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const iconPx = size === "sm" ? 17 : 20;
  const colors =
    tone === "primary"
      ? "bg-primary-700 hover:bg-primary-800 focus-visible:ring-primary-700"
      : "bg-accent-700 hover:bg-accent-800 focus-visible:ring-accent-600";
  return (
    <Link
      to={to}
      aria-label={ariaLabel}
      className={`inline-flex shrink-0 items-center justify-center rounded-lg text-white transition focus-visible:outline-none focus-visible:ring-2 ${shape} ${colors} ${className}`}
    >
      <svg viewBox="0 0 24 24" width={iconPx} height={iconPx} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </Link>
  );
}

/**
 * Groene "in winkelwagen"-knop met wit winkelwagentje. Standaard icon-only
 * (compact, bv. op productkaarten); geef `label` mee voor icoon + tekst.
 * In `added`-staat toont 'ie een vinkje en is 'ie uitgeschakeld.
 */
export function AddToCartButton({
  onClick,
  added = false,
  label,
  ariaLabel,
  size = "md",
  className = "",
}: {
  onClick: () => void;
  added?: boolean;
  /** Optionele tekst naast het icoon (bv. "Alles toevoegen"). Weglaten = icon-only. */
  label?: string;
  /** Verplicht bij icon-only (zonder zichtbare tekst) voor toegankelijkheid. */
  ariaLabel?: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const t = useT();
  const iconPx = size === "sm" ? 17 : 20;
  // Met label = pill (icoon + tekst); zonder label = klein vierkantje (icon-only).
  const shape = label
    ? size === "sm"
      ? "min-h-9 px-3 text-xs"
      : "min-h-11 px-5 text-sm"
    : size === "sm"
      ? "h-9 w-9"
      : "h-11 w-11";
  const tone = added
    ? "bg-success-500 hover:bg-success-500"
    : "bg-buy-600 hover:bg-buy-700 focus-visible:ring-2 focus-visible:ring-buy-600";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={added}
      aria-label={ariaLabel ? t(ariaLabel) : (label ? undefined : t("In winkelwagen"))}
      className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-semibold text-white transition focus-visible:outline-none disabled:cursor-default ${shape} ${tone} ${className}`}
    >
      {added ? (
        <svg viewBox="0 0 24 24" width={iconPx} height={iconPx} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12l5 5L20 6" />
        </svg>
      ) : (
        <CartIcon size={iconPx} />
      )}
      {label && <span>{added ? t("Toegevoegd") : t(label)}</span>}
    </button>
  );
}

/** Accent CTA rendered as a router link (the one conversion colour, BRANDING §7). */
export function CtaLink({ to, children, className = "" }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-12 items-center justify-center bg-accent-700 px-7 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-accent-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 ${className}`}
    >
      {children}
    </Link>
  );
}

/** Secondary link button (outline). */
export function GhostLink({ to, children, className = "" }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-12 items-center justify-center border-2 border-primary-700 px-7 text-sm font-bold uppercase tracking-wide text-primary-700 transition hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${className}`}
    >
      {children}
    </Link>
  );
}

/** Standard heading block for content pages. */
export function PageHeader({ title, intro }: { title: string; intro?: ReactNode }) {
  return (
    <header className="max-w-2xl">
      <h1 className="text-3xl font-bold text-primary-900">{title}</h1>
      {intro && <p className="mt-3 text-primary-600">{intro}</p>}
    </header>
  );
}

/**
 * Readable long-form content wrapper. Styles common child elements so content
 * pages can be written as plain JSX without per-element class noise.
 */
export function Prose({ children }: { children: ReactNode }) {
  return (
    <div className="prose-glas mt-6 max-w-2xl space-y-4 text-primary-700 [&_a]:font-medium [&_a]:text-primary-700 [&_a]:underline [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-primary-900 [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-primary-900 [&_li]:ml-1 [&_p]:leading-relaxed [&_strong]:text-primary-900 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
      {children}
    </div>
  );
}

/**
 * Page-level container. Default = de standaard content-band (`max-w-[1600px]`),
 * gelijk aan de homepage en de rest van de site — zo starten alle pagina's vanaf
 * dezelfde breedte. Geef een smallere `className` mee voor uitzonderingen.
 */
export function Container({ children, className = "max-w-[1600px]" }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto px-4 py-10 ${className}`}>{children}</div>;
}
